import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Loader2, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNotifications, NotificationType, useUnreadNotificationsCount } from '../hooks/useNotifications';
import { useMarkNotificationRead } from '../hooks/useMarkNotificationRead';
import { NotificationItem } from '../components/NotificationItem';

const FILTER_TABS: { value: NotificationType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'reaction', label: 'Reactions' },
  { value: 'comment', label: 'Comments' },
  { value: 'mention', label: 'Mentions' },
  { value: 'follow', label: 'Follows' },
];

export function NotificationsContent() {
  const [filter, setFilter] = useState<NotificationType>('all');
  const { 
    notifications, 
    isLoading, 
    isFetchingNextPage, 
    hasNextPage, 
    fetchNextPage,
    refetch,
    error 
  } = useNotifications(filter);
  const { data: unreadCount } = useUnreadNotificationsCount();
  const { markAllAsRead, isMarkingAllRead } = useMarkNotificationRead();
  
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  // Pull to refresh simulation
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with Mark All Read */}
      <div className="sticky top-0 z-40 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold text-foreground">Notifications</h1>
          {unreadCount && unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead()}
              disabled={isMarkingAllRead}
              className="text-primary"
            >
              {isMarkingAllRead ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <CheckCheck className="h-4 w-4 mr-1" />
              )}
              Mark all read
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex overflow-x-auto gap-1 pb-1 scrollbar-hide">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                filter === tab.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Refresh indicator */}
      {isRefreshing && (
        <div className="flex justify-center py-2 bg-background">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p className="text-destructive text-center">Failed to load notifications</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleRefresh}>
              Try again
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="bg-muted rounded-full p-4 mb-4">
              <Bell className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-foreground font-medium text-center">No notifications yet</p>
            <p className="text-muted-foreground text-sm text-center mt-1">
              Start engaging with posts to see activity here!
            </p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}

            {/* Load More Trigger */}
            <div ref={loadMoreRef} className="py-4">
              {isFetchingNextPage && (
                <div className="flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
              {!hasNextPage && notifications.length > 0 && (
                <p className="text-center text-muted-foreground text-sm">
                  You've seen all notifications
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
